import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MdKeyboardArrowRight, MdKeyboardArrowLeft } from "react-icons/md";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const API_URL =
  "https://customizebychoice.com/pikchar_api/data/filenamee/titles_with_posters";

const INITIAL_CATEGORIES = 3;
const INITIAL_POSTERS_PER_CATEGORY = 7;
const POSTERS_TO_LOAD_MORE = 6;

// Optimize Amazon image URLs
const modifyAmazonImageUrl = (originalUrl, width = 400) => {
  if (!originalUrl) return null;

  if (
    originalUrl.startsWith("https://m.media-amazon.com/images") &&
    originalUrl.endsWith(".jpg")
  ) {
    if (originalUrl.includes("FMjpg_UX")) {
      return originalUrl.replace(/FMjpg_UX\d+_/g, `FMjpg_UX${width}_`);
    }

    if (originalUrl.includes("._V1_.")) {
      return originalUrl.replace("._V1_.", `._V1_FMjpg_UX${width}_.`);
    }
  }

  return originalUrl;
};

const HomeList = () => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [visibleCategories, setVisibleCategories] = useState(INITIAL_CATEGORIES);
  const [visiblePosters, setVisiblePosters] = useState({});
  const navigate = useNavigate();
  const categoryObserverRef = useRef(null);
  const posterObserversRef = useRef({});

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(API_URL, { cache: "no-cache" });
        const json = await res.json();

        if (json?.success && json?.data) {
          setData(json.data);

          const initialVisible = {};
          Object.keys(json.data).forEach(catId => {
            initialVisible[catId] = INITIAL_POSTERS_PER_CATEGORY;
          });
          setVisiblePosters(initialVisible);
        }
      } catch (e) {
        console.error("Failed to load titles:", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Load more categories
  const lastCategoryRef = useCallback(
    (node) => {
      if (loading) return;
      if (categoryObserverRef.current) categoryObserverRef.current.disconnect();

      categoryObserverRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCategories(prev =>
            Math.min(prev + 2, Object.keys(data).length)
          );
        }
      });

      if (node) categoryObserverRef.current.observe(node);
    },
    [loading, data]
  );

  // Load more posters
  const createPosterObserver = useCallback((categoryId, totalPosters) => {
    return (node) => {
      if (!node) return;

      if (posterObserversRef.current[categoryId]) {
        posterObserversRef.current[categoryId].disconnect();
      }

      posterObserversRef.current[categoryId] = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setVisiblePosters(prev => ({
              ...prev,
              [categoryId]: Math.min(
                (prev[categoryId] || INITIAL_POSTERS_PER_CATEGORY) +
                POSTERS_TO_LOAD_MORE,
                totalPosters
              ),
            }));
          }
        },
        { rootMargin: '200px' }
      );

      posterObserversRef.current[categoryId].observe(node);
    };
  }, []);

  const slideLeft = (categoryId) => {
    const slider = document.getElementById(`slider-${categoryId}`);
    if (slider) slider.scrollBy({ left: -500, behavior: "smooth" });
  };

  const slideRight = (categoryId) => {
    const slider = document.getElementById(`slider-${categoryId}`);
    if (slider) slider.scrollBy({ left: 500, behavior: "smooth" });
  };

  useEffect(() => {
    return () => {
      if (categoryObserverRef.current) categoryObserverRef.current.disconnect();
      Object.values(posterObserversRef.current).forEach(obs => obs.disconnect());
    };
  }, []);

  return (
    <div className="md:ml-28 ml-0 relative mt-0 z-10">

      {loading ? (
        Array(3).fill().map((_, index) => (
          <div key={index} className="mb-4">
            <div className="px-4 mb-2">
              <div className="md:w-[400px] w-[100px] h-[26px] bg-gray-300 rounded-md animate-pulse" />
            </div>

            <div className="flex overflow-x-auto gap-4 md:h-[290px] h-[220px] items-center px-5">
              {Array(12).fill().map((_, i) => (
                <div key={i} className="md:w-[180px] md:h-[230px] w-[130px] h-[180px]">
                  <Skeleton height="100%" />
                </div>
              ))}
            </div>
          </div>
        ))
      ) : (
        Object.keys(data)
          .slice(0, visibleCategories)
          .map((categoryId, categoryIndex) => {

            const cat = data[categoryId] || {};
            const categoryTitle = cat.title || `Category ${categoryId}`;
            const isLatestAdded = cat.isLatest === true;
            const isLatestEpisodes =
              categoryTitle.toLowerCase() === "New Episodes Added";

            const length = parseInt(cat.length, 10) || 0;

            const allItems = [];
            for (let i = 1; i <= length; i++) {
              const entry = cat[i.toString()];
              if (entry?.poster) {
                allItems.push({
                  code: entry.code,
                  poster: modifyAmazonImageUrl(entry.poster, 400),
                  value: entry.value || null
                });
              }
            }

            if (!allItems.length) return null;

            const currentVisible = visiblePosters[categoryId] || INITIAL_POSTERS_PER_CATEGORY;
            const items = allItems.slice(0, currentVisible);
            const hasMore = currentVisible < allItems.length;
            const isLastCategory = categoryIndex === visibleCategories - 1;

            return (
              <div
                key={categoryId}
                className={`mb-4 ${isLatestAdded ? 'bg-purple-900/30 py-4 rounded-lg' : ''}`}
                ref={isLastCategory && visibleCategories < Object.keys(data).length ? lastCategoryRef : null}
              >

                {/* Title */}
                <div className="px-4 mb-2">
                  <p className={`font-bold sm:text-2xl text-[13px] ${isLatestAdded ? 'text-purple-300' : ''}`}>
                    {categoryTitle}
                  </p>
                </div>

                {/* Posters */}
                <div className="relative">

                  <div
                    onClick={() => slideLeft(categoryId)}
                    className="absolute left-0 px-7 md:h-[290px] h-[220px] flex items-center cursor-pointer z-30"
                  >
                    <MdKeyboardArrowLeft className="text-white text-3xl" />
                  </div>

                  <div
                    id={`slider-${categoryId}`}
                    className="flex overflow-x-auto gap-4 md:h-[290px] h-[190px] items-center px-5 scrollbar-hide"
                  >
                    {items.map((item, index) => {
                      const isLastVisible = index === items.length - 1 && hasMore;
                      return (
                        <div
                          key={item.code}
                          ref={isLastVisible ? createPosterObserver(categoryId, allItems.length) : null}
                          className="flex-shrink-0"
                        >
                          <div className={`relative md:w-[180px] md:h-[230px] sm:w-[150px] sm:h-[200px] w-[105px] h-[150px] overflow-hidden rounded-lg ${isLatestAdded ? 'ring-2 ring-purple-400' : ''}`}>

                            {/* VALUE BADGE */}
                            {isLatestEpisodes && item.value && (
                              <div className="absolute top-1 right-1 bg-purple-600 text-white text-[10px] sm:text-xs px-2 py-[2px] rounded-md font-semibold shadow-md z-20">
                                {item.value}
                              </div>
                            )}

                            <Link to={`/main-list/${item.code}`}>
                              <img
                                src={item.poster}
                                alt={item.code}
                                loading="lazy"
                                className="w-full h-full rounded-lg transition-transform duration-300 sm:hover:scale-[1.15]"
                              />
                            </Link>
                          </div>
                        </div>
                      );
                    })}

                    {hasMore && (
                      <div className="md:w-[180px] md:h-[230px] w-[105px] h-[150px]">
                        <Skeleton height="100%" />
                      </div>
                    )}
                  </div>

                  <div
                    onClick={() => slideRight(categoryId)}
                    className="absolute right-0 flex items-center h-[280px] cursor-pointer z-30"
                  >
                    <MdKeyboardArrowRight className="text-white text-3xl mr-8" />
                  </div>

                </div>
              </div>
            );
          })
      )}
    </div>
  );
};

export default HomeList;
